import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChevronDown,
  FaChevronUp,
  FaWhatsapp,
  FaQuestionCircle,
  FaEnvelope,
  FaUserFriends,
  FaClock,
  FaShieldAlt,
  FaVideo,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaSearch,
  FaTimes,
  FaCalendarAlt,
  FaAward,
  FaComments
} from 'react-icons/fa';
import Header from './components/Header';
import Footer from './components/Footer';
import { WHATSAPP_NUMBER } from './config/navItems';
import { useLanguage } from './i18n';

const FAQPage = () => {
  const { t, tArray } = useLanguage();
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    document.title = "FAQ - Wind Tunnel Coaching | Frequently Asked Questions";

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleWhatsAppRedirect = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const faqCategories = [
    {
      id: 'getting-started',
      title: t('faqPage.categories.gettingStarted.title'),
      icon: <FaGraduationCap className="text-orange-500" />,
      color: "from-orange-500 to-red-500",
      questions: tArray('faqPage.categories.gettingStarted.questions')
    },
    {
      id: 'sessions-training',
      title: t('faqPage.categories.sessionsTraining.title'),
      icon: <FaClock className="text-blue-500" />,
      color: "from-blue-500 to-purple-500",
      questions: tArray('faqPage.categories.sessionsTraining.questions')
    },
    {
      id: 'location-equipment',
      title: t('faqPage.categories.locationEquipment.title'),
      icon: <FaMapMarkerAlt className="text-green-500" />,
      color: "from-green-500 to-teal-500",
      questions: tArray('faqPage.categories.locationEquipment.questions')
    },
    {
      id: 'safety-preparation',
      title: t('faqPage.categories.safetyPreparation.title'),
      icon: <FaShieldAlt className="text-purple-500" />,
      color: "from-purple-500 to-pink-500",
      questions: tArray('faqPage.categories.safetyPreparation.questions')
    },
    {
      id: 'video-progress',
      title: t('faqPage.categories.videoProgress.title'),
      icon: <FaVideo className="text-red-500" />,
      color: "from-red-500 to-orange-500",
      questions: tArray('faqPage.categories.videoProgress.questions')
    },
    {
      id: 'booking-policies',
      title: t('faqPage.categories.bookingPolicies.title'),
      icon: <FaCalendarAlt className="text-indigo-500" />,
      color: "from-indigo-500 to-blue-500",
      questions: tArray('faqPage.categories.bookingPolicies.questions')
    }
  ];

  // Filter questions based on search term and category
  const filteredCategories = faqCategories.filter(category => {
    if (selectedCategory !== 'all' && category.id !== selectedCategory) return false;

    if (searchTerm) {
      return category.questions.some(q =>
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  }).map(category => ({
    ...category,
    questions: category.questions.filter(q =>
      !searchTerm ||
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900 relative overflow-hidden">
      {/* Interactive Wind Tunnel Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Animated Wind Flow Lines */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute h-px wind-flow-line"
              style={{
                top: `${10 + i * 8}%`,
                left: `-100px`,
                right: `-100px`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + i * 0.2}s`
              }}
            />
          ))}
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(25)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 rounded-full floating-particle"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${6 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        {/* Rotating Air Currents */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full air-current-ring"
              style={{
                top: `${15 + i * 12}%`,
                left: `${8 + i * 12}%`,
                width: `${80 + i * 40}px`,
                height: `${80 + i * 40}px`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${12 + i * 3}s`
              }}
            />
          ))}
        </div>

        {/* Gradient Orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-gradient-to-r from-red-500/15 to-orange-500/15 rounded-full blur-2xl animate-pulse delay-3000"></div>
          <div className="absolute bottom-40 left-20 w-80 h-80 bg-gradient-to-r from-green-500/15 to-teal-500/15 rounded-full blur-3xl animate-pulse delay-4000"></div>
        </div>
      </div>

      {/* Interactive Cursor Effect */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div
          className="wind-cursor-effect"
          style={{
            left: mousePosition.x - 100,
            top: mousePosition.y - 100,
          }}
        ></div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes windFlow {
          0% { transform: translateX(-100px) scaleX(0); opacity: 0; }
          50% { transform: translateX(50vw) scaleX(1); opacity: 1; }
          100% { transform: translateX(100vw) scaleX(0); opacity: 0; }
        }

        @keyframes floatUp {
          0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(-100px) rotate(360deg); opacity: 0; }
        }

        @keyframes windTurbulence {
          0%, 100% { transform: translateX(0) translateY(0) rotate(0deg); }
          25% { transform: translateX(10px) translateY(-5px) rotate(90deg); }
          50% { transform: translateX(-5px) translateY(5px) rotate(180deg); }
          75% { transform: translateX(5px) translateY(-10px) rotate(270deg); }
        }

        @keyframes airFlow {
          0% { transform: translateX(-50px) scaleY(0.5); opacity: 0; }
          50% { transform: translateX(0) scaleY(1); opacity: 0.8; }
          100% { transform: translateX(50px) scaleY(0.5); opacity: 0; }
        }

        @keyframes tunnelSpin {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.1); }
          100% { transform: rotate(360deg) scale(1); }
        }

        .wind-cursor-effect {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(249, 115, 22, 0.15) 0%, rgba(59, 130, 246, 0.1) 50%, transparent 100%);
          animation: windTurbulence 3s ease-in-out infinite;
          transition: all 0.3s ease;
          pointer-events: none;
        }

        .wind-cursor-effect::before {
          content: '';
          position: absolute;
          inset: 20px;
          border-radius: 50%;
          background: conic-gradient(from 0deg, transparent, rgba(249, 115, 22, 0.3), transparent);
          animation: tunnelSpin 4s linear infinite;
        }

        .wind-cursor-effect::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: linear-gradient(45deg, transparent 40%, rgba(59, 130, 246, 0.2) 50%, transparent 60%);
          animation: airFlow 2s ease-in-out infinite;
        }

        /* Enhanced wind flow lines */
        .wind-flow-line {
          animation: windFlow 4s linear infinite;
          background: linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.4), rgba(59, 130, 246, 0.3), transparent);
        }

        /* Floating particles enhancement */
        .floating-particle {
          animation: floatUp 8s linear infinite;
          background: radial-gradient(circle, rgba(249, 115, 22, 0.6) 0%, transparent 70%);
        }

        .floating-particle:nth-child(even) {
          animation-direction: reverse;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.6) 0%, transparent 70%);
        }

        /* Air current rings */
        .air-current-ring {
          border: 2px solid rgba(249, 115, 22, 0.3);
          animation: tunnelSpin 15s linear infinite;
        }

        .air-current-ring:nth-child(even) {
          border-color: rgba(59, 130, 246, 0.3);
          animation-direction: reverse;
          animation-duration: 12s;
        }

        /* Responsive optimizations */
        @media (max-width: 768px) {
          .wind-cursor-effect {
            width: 150px;
            height: 150px;
            opacity: 0.7;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .wind-cursor-effect,
          .wind-flow-line,
          .floating-particle,
          .air-current-ring {
            animation: none;
          }
        }
      `}</style>

      {/* Header Component */}
      <Header />

      {/* Hero Section */}
      <div className="pt-32 pb-16 md:pt-40 md:pb-20 px-4 relative">
        <div className="max-w-6xl mx-auto text-center relative z-20">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-10"
          >
            <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 p-6 rounded-3xl mb-8 shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105">
              <FaQuestionCircle className="text-white text-5xl md:text-7xl" />
            </div>
          </motion.div>

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 md:mb-8"
          >
            {t('faqPage.title')}{' '}
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              {t('faqPage.titleHighlight')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-lg md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed"
          >
            {t('faqPage.subtitle')}
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-2xl mx-auto relative"
          >
            <div className="relative group">
              <FaSearch className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl group-hover:text-orange-400 transition-colors" />
              <input
                type="text"
                placeholder={t('faqPage.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-16 pr-6 py-5 bg-gray-800/60 backdrop-blur-lg border border-orange-500/30 rounded-3xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-lg shadow-xl hover:shadow-orange-500/20 hover:border-orange-500/50"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors hover:scale-110"
                >
                  <FaTimes className="text-xl" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-6xl mx-auto px-4 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-20"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('faqPage.browseByCategory')} <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">{t('faqPage.categoryHighlight')}</span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('faqPage.categoryDescription')}
            </p>
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-2xl text-base font-medium transition-all transform hover:scale-105 shadow-lg hover:shadow-orange-500/20 ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-orange-500/30'
                  : 'bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 backdrop-blur-sm border border-gray-600/50 hover:border-orange-500/30'
              }`}
            >
              <FaQuestionCircle className="mr-2 inline transition-transform hover:scale-110" />
              {t('faqPage.allCategories')}
            </button>
            {faqCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-2xl text-base font-medium transition-all transform hover:scale-105 shadow-lg hover:shadow-orange-500/20 flex items-center ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-orange-500/30'
                    : 'bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 backdrop-blur-sm border border-gray-600/50 hover:border-orange-500/30'
                }`}
              >
                <span className="mr-2 transition-transform hover:scale-110">{category.icon}</span>
                {category.title}
                <span className="ml-2 bg-white/20 text-xs px-2 py-1 rounded-full transition-all hover:bg-white/30">
                  {category.questions.length}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* FAQ Categories */}
      <div className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        <div className="space-y-12 relative z-20">
          {filteredCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="bg-gray-800/40 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-orange-500/20 hover:border-orange-500/40 transition-all group shadow-2xl hover:shadow-orange-500/10"
            >
              {/* Category Header */}
              <div className="flex items-center mb-10">
                <div className={`bg-gradient-to-r ${category.color} p-5 rounded-3xl mr-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <div className="text-white text-2xl">
                    {category.icon}
                  </div>
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white group-hover:text-orange-400 transition-colors">
                    {category.title}
                  </h2>
                  <p className="text-gray-400 text-base mt-2">
                    {category.questions.length} {category.questions.length !== 1 ? t('faqPage.questionsCountPlural') : t('faqPage.questionsCount')} • {t('faqPage.getInstantAnswers')}
                  </p>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-6">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = `${category.id}-${faqIndex}`;
                  const isExpanded = expandedFAQ === globalIndex;

                  return (
                    <motion.div
                      key={faqIndex}
                      layout
                      className="border-b border-gray-700/30 last:border-b-0 pb-6 last:pb-0"
                    >
                      <button
                        onClick={() => toggleFAQ(globalIndex)}
                        className="w-full flex items-center justify-between text-left py-6 hover:bg-gray-700/10 rounded-3xl px-6 transition-all group/item hover:shadow-lg hover:shadow-orange-500/10"
                      >
                        <div className="flex items-center flex-1">
                          <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-4 rounded-2xl mr-6 group-hover/item:from-orange-500/30 group-hover/item:to-red-500/30 transition-all group-hover/item:shadow-lg group-hover/item:shadow-orange-500/20">
                            <FaQuestionCircle className="text-orange-500 text-xl group-hover/item:scale-110 transition-transform" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl md:text-2xl font-semibold text-white group-hover/item:text-orange-400 transition-colors text-left">
                              {faq.question}
                            </h3>
                            {faq.popular && (
                              <span className="inline-block bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 px-3 py-1 rounded-full text-sm font-medium mt-3 border border-orange-500/30">
                                {t('faqPage.popular')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-6">
                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-3 rounded-xl group-hover/item:from-orange-500/30 group-hover/item:to-red-500/30 transition-all group-hover/item:shadow-lg group-hover/item:shadow-orange-500/20"
                          >
                            <FaChevronDown className="text-orange-500 text-lg group-hover/item:scale-110 transition-transform" />
                          </motion.div>
                        </div>
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6">
                              <div className="bg-gradient-to-r from-gray-700/40 to-gray-800/40 backdrop-blur-sm rounded-3xl p-8 border border-orange-500/10 shadow-inner">
                                <div className="text-gray-100 leading-relaxed text-lg">
                                  {faq.answer.split('\n').map((line, lineIndex) => {
                                    if (line.trim() === '') {
                                      return <div key={lineIndex} className="h-4"></div>;
                                    } else if (line.startsWith('✅')) {
                                      return (
                                        <div key={lineIndex} className="flex items-start mb-4">
                                          <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-full mr-4 mt-2 flex-shrink-0 shadow-md"></div>
                                          <span className="text-gray-100">{line.substring(2)}</span>
                                        </div>
                                      );
                                    } else if (line.startsWith('- ')) {
                                      return (
                                        <div key={lineIndex} className="flex items-start mb-4">
                                          <div className="w-3 h-3 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mr-4 mt-2 flex-shrink-0 shadow-md"></div>
                                          <span className="text-gray-100">{line.substring(2)}</span>
                                        </div>
                                      );
                                    } else if (line.includes('**') && line.includes('**')) {
                                      const parts = line.split('**');
                                      return (
                                        <p key={lineIndex} className="mb-4 text-gray-100">
                                          {parts.map((part, partIndex) =>
                                            partIndex % 2 === 1 ? (
                                              <span key={partIndex} className="font-bold text-orange-400 bg-orange-500/20 px-3 py-1 rounded-lg border border-orange-500/30">{part}</span>
                                            ) : (
                                              <span key={partIndex}>{part}</span>
                                            )
                                          )}
                                        </p>
                                      );
                                    } else {
                                      return (
                                        <p key={lineIndex} className="mb-4 text-gray-100">
                                          {line}
                                        </p>
                                      );
                                    }
                                  })}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Still Have Questions Section */}
      <div className="max-w-5xl mx-auto px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-xl rounded-3xl p-12 md:p-16 text-center border border-orange-500/30 relative overflow-hidden shadow-2xl"
        >
          {/* Enhanced animated background elements */}
          <div className="absolute top-0 left-0 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-red-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl animate-pulse delay-2000"></div>

          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="inline-block bg-gradient-to-r from-orange-500 to-red-500 p-6 rounded-3xl mb-8 shadow-xl shadow-orange-500/30"
            >
              <FaComments className="text-white text-4xl md:text-5xl" />
            </motion.div>

            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-3xl md:text-4xl font-bold text-white mb-6"
            >
              {t('faqPage.stillHaveQuestions.title')}
            </motion.h2>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="text-gray-200 mb-10 text-xl max-w-3xl mx-auto leading-relaxed"
            >
              {t('faqPage.stillHaveQuestions.description')}
            </motion.p>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-6 justify-center"
            >
              <button
                onClick={() => handleWhatsAppRedirect("Hi! I have a question about wind tunnel coaching that wasn't covered in the FAQ.")}
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-5 rounded-3xl transition-all transform hover:scale-105 flex items-center justify-center text-xl font-bold shadow-xl hover:shadow-green-500/30 backdrop-blur-sm"
              >
                <FaWhatsapp className="mr-3 text-2xl" />
                {t('faqPage.stillHaveQuestions.whatsappButton')}
              </button>
              <a
                href="/contact"
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-10 py-5 rounded-3xl hover:shadow-xl hover:shadow-orange-500/50 transition-all transform hover:scale-105 flex items-center justify-center text-xl font-bold"
              >
                <FaEnvelope className="mr-3 text-2xl" />
                {t('faqPage.stillHaveQuestions.contactButton')}
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default FAQPage;
